import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';
import {
  EDGE_CLAMP,
  FILTER_AREA,
  FILTER_BILINEAR,
  type EdgeMode,
  type FilterMode,
  type Rect,
} from '../types.js';

export interface ResampleOptions {
  filter?: FilterMode;
  edge?: EdgeMode;
  /** Colour returned outside the source when `edge` is `EDGE_PAD`. */
  padColor?: readonly [number, number, number, number];
  /**
   * Filter on premultiplied colour. Required whenever the source has meaningful
   * transparency, otherwise fully transparent texels drag their (arbitrary) RGB
   * into the edge of the artwork.
   */
  premultiplied?: boolean;
  /** Region of the source to map onto the whole destination. */
  srcRect?: Rect;
}

/**
 * Maps `srcRect` (default: all of `src`) onto the whole of `dst`.
 *
 * Non-uniform scaling is intentional - RMBG-1.4 is trained on squashed 1024x1024
 * inputs, so matching that beats letterboxing, and the matte is un-squashed on
 * the way back out.
 */
export function resample(
  ctx: GpuContext,
  src: GpuImage,
  dst: GpuImage,
  options: ResampleOptions = {},
): void {
  const rect = options.srcRect ?? { x: 0, y: 0, width: src.width, height: src.height };
  const pad = options.padColor ?? [0, 0, 0, 0];

  const params = new Struct(64)
    .i(0, src.width, src.height)
    .i(8, dst.width, dst.height)
    .f(16, rect.width / dst.width, rect.height / dst.height)
    .f(24, rect.x, rect.y)
    .f(32, pad[0], pad[1], pad[2], pad[3])
    .u(48, options.filter ?? FILTER_BILINEAR, options.edge ?? EDGE_CLAMP, options.premultiplied ? 1 : 0, 0);

  ctx.dispatch(
    {
      module: 'resample.wgsl',
      defines: { DST_FORMAT: dst.format },
      bindings: [
        { kind: 'texture', format: src.format },
        { kind: 'storageTexture', format: dst.format },
        { kind: 'uniform' },
      ],
    },
    [src, dst, ctx.uniform(params.bytes)],
    [workgroupCount(dst.width, 8), workgroupCount(dst.height, 8)],
    `resample ${src.width}x${src.height}->${dst.width}x${dst.height}`,
  );
}

/** Convenience: allocate the destination, resample into it, return it. */
export function resampleTo(
  ctx: GpuContext,
  src: GpuImage,
  width: number,
  height: number,
  options: ResampleOptions = {},
): GpuImage {
  const dst = ctx.image(width, height, src.format, 'resampled');
  resample(ctx, src, dst, options);
  return dst;
}

export interface ContainFit {
  /** Uniform scale applied to the source. */
  scale: number;
  /** Destination-space offset of the scaled content. */
  offsetX: number;
  offsetY: number;
  contentWidth: number;
  contentHeight: number;
}

/** Aspect-preserving fit of `src` inside `dst`, used by letterboxing models. */
export function fitContain(
  srcWidth: number,
  srcHeight: number,
  dstWidth: number,
  dstHeight: number,
): ContainFit {
  const scale = Math.min(dstWidth / srcWidth, dstHeight / srcHeight);
  const contentWidth = Math.round(srcWidth * scale);
  const contentHeight = Math.round(srcHeight * scale);
  return {
    scale,
    offsetX: Math.floor((dstWidth - contentWidth) / 2),
    offsetY: Math.floor((dstHeight - contentHeight) / 2),
    contentWidth,
    contentHeight,
  };
}

/**
 * Downscales through a mip chain when the ratio is large.
 *
 * A single area-filter dispatch caps at 32 taps per axis; beyond roughly 8x,
 * halving repeatedly is both faster and better behaved (no aliasing from the
 * strided fallback inside `resample.wgsl`).
 */
export function downscale(
  ctx: GpuContext,
  src: GpuImage,
  width: number,
  height: number,
  options: ResampleOptions = {},
): GpuImage {
  let current = src;
  let owned = false;
  while (current.width >= width * 2 && current.height >= height * 2) {
    const next = ctx.image(
      Math.max(width, current.width >> 1),
      Math.max(height, current.height >> 1),
      src.format,
      'downscale',
    );
    resample(ctx, current, next, { ...options, filter: FILTER_AREA });
    if (owned) current.release();
    current = next;
    owned = true;
  }
  const dst = ctx.image(width, height, src.format, 'downscale');
  resample(ctx, current, dst, { ...options, filter: options.filter ?? FILTER_AREA });
  if (owned) current.release();
  return dst;
}
